import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, Form, Button } from "react-bootstrap";
import { useGlobalState } from "../../services/hostelConst";
import {
  addMaintenance,
  getMaintenance,
  updateMaintenance,
} from "../../services/OrganisationServices/transportManagemetService";
import { callToast, showErrorMessages } from "../../services/utilityFunctions";

const AddMaintenanceForm = ({ navigate, refresh }) => {
  const { closeModal, masterTableState, updateMasterTable, modalState } =
    useGlobalState();
  const id = modalState.id;

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(modalState.isEditing);

  const prepareFormData = (data = null) => {
    return {
      vehicleId: data == null ? "" : data.VEHICLE_ID || data._VEHICLE || "",
      maintenanceType: data == null ? "" : data.MAINTENANCE_TYPE || "",
      description: data == null ? "" : data.DESCRIPTION || "",
      cost: data == null ? "" : data.COST || "",
      maintenanceDate: data == null ? "" : data.MAINTENANCE_DATE || "",
      nextMaintenanceDate: data == null ? "" : data.NEXT_MAINTENANCE_DATE || "",
      status: data == null ? "PENDING" : data.STATUS || "PENDING",
    };
  };

  const [formData, setFormData] = useState(prepareFormData());

  const getMaintenanceDetailsData = async (id) => {
    setLoadingData(true);
    try {
      const res = await getMaintenance(id);
      if (res.status === "success") {
        setFormData(prepareFormData(res.data));
      } else {
        showErrorMessages({ data: res, navigate });
        closeModal();
      }
    } catch (error) {
      console.error("Error fetching maintenance details:", error);
      showErrorMessages({
        data: { message: "Failed to load maintenance details" },
        navigate,
      });
      closeModal();
    }
    setLoadingData(false);
  };

  useEffect(() => {
    if (modalState.isEditing && id) {
      getMaintenanceDetailsData(id);
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.vehicleId.trim()) {
      newErrors.vehicleId = "Vehicle ID is required";
    }
    if (!formData.maintenanceType.trim()) {
      newErrors.maintenanceType = "Maintenance type is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }
    if (!formData.cost) {
      newErrors.cost = "Cost is required";
    }
    if (!formData.maintenanceDate) {
      newErrors.maintenanceDate = "Maintenance date is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        vehicleId: formData.vehicleId,
        maintenanceType: formData.maintenanceType,
        description: formData.description,
        cost: parseFloat(formData.cost),
        maintenanceDate: formData.maintenanceDate,
        nextMaintenanceDate: formData.nextMaintenanceDate,
        status: formData.status,
      };

      let res;
      if (modalState.isEditing) {
        submitData.entityId = id;
        res = await updateMaintenance(submitData);
      } else {
        res = await addMaintenance(submitData);
      }

      if (res.status === "success") {
        callToast(
          "success",
          "Success",
          `Maintenance ${
            modalState.isEditing ? "updated" : "added"
          } successfully`
        );
        updateMasterTable(true);
        closeModal();
      } else {
        showErrorMessages({ data: res, navigate });
      }
    } catch (error) {
      console.error("Error submitting maintenance:", error);
    }
    setLoading(false);
  };

  if (loadingData) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <h5 className="card-title">
          {modalState.isEditing ? "Edit" : "Add"} Maintenance
        </h5>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Vehicle ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.vehicleId}
                  onChange={(e) => handleChange("vehicleId", e.target.value)}
                  isInvalid={!!errors.vehicleId}
                  placeholder="Enter vehicle ID"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.vehicleId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Maintenance Type *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.maintenanceType}
                  onChange={(e) =>
                    handleChange("maintenanceType", e.target.value)
                  }
                  isInvalid={!!errors.maintenanceType}
                  placeholder="Enter maintenance type"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.maintenanceType}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  isInvalid={!!errors.description}
                  placeholder="Enter maintenance description"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Cost *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => handleChange("cost", e.target.value)}
                  isInvalid={!!errors.cost}
                  placeholder="Enter cost"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.cost}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Maintenance Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.maintenanceDate}
                  onChange={(e) =>
                    handleChange("maintenanceDate", e.target.value)
                  }
                  isInvalid={!!errors.maintenanceDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.maintenanceDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Next Maintenance Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.nextMaintenanceDate}
                  onChange={(e) =>
                    handleChange("nextMaintenanceDate", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  <option value="PENDING">Pending</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {modalState.isEditing ? "Updating..." : "Adding..."}
                </>
              ) : modalState.isEditing ? (
                "Update Maintenance"
              ) : (
                "Add Maintenance"
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddMaintenanceForm;
